import type { Router } from "vue-router";
import { userSidebarStore } from "@/app/store/SidebarStore";
import { useLoginStore } from "@/app/store/LoginStore";
import { nextTick } from "vue";
import { setUserSidebar } from "@/app/util/route";
import { verifyRouter, verifySession } from "@/api/login/LoginApi";

export const navigationGuard = (router: Router): void => {
  // 앱 타이틀(환경변수) — document.title 뒤에 붙임
  const appTitle = import.meta.env.VITE_APP_TITLE;
  const sidebarStore = userSidebarStore();
  const loginStore = useLoginStore();

  // app load후 첫 진입때 세션체크
  router.beforeEach(async (to, _from, next) => {

    const toMeta = to.meta;
    console.info("🚀", _from.fullPath, "→", to.fullPath);

    // 1) 에러페이지는 바로 통과
    if (to.name === "noServer" || to.name === "error") {
      return next();
    }

    // 2) 세션 검증이 없으면 세션 검증 수행(새로고침 및 첫진입)
    if (!loginStore.getSessionChk) {
      const res = await verifySession();
      if (res != null) loginStore.setLoginInfo(res);
      loginStore.setSessionChk(true);
    }

    // 3) 로그인 정보가 있고 메뉴 구성이 안 되어 있으면 메뉴 구성
    if (loginStore.getLoginInfo && !sidebarStore.getRouteYn) {
      const res = await setUserSidebar();
      if (!res) return next(false);
      // 메뉴 구성 후 현재 경로로 재진입(히스토리 중복 방지: replace)
      await nextTick();
      return next({ ...to, replace: true });
    }

    // 4) 정적 라우트는 권한 체크 없이 통과
    if (Object.keys(toMeta).length !== 0 && !toMeta.isDynamic) {
      return next();
    }

    // 5) 서버에 이 라우트에 대한 권한이 있는지 확인
    const res = await verifyRouter({ url: to.path, menuCd: toMeta.menuCd });
    if (res) {
      await loginStore.setApiAuth(res);
      console.info("[Allowed method in '" + String(to.path) + "'] " + loginStore.getApiAuth);
      return next();
    }
  });

  router.afterEach(async (to) => {
    const toMeta = to.meta;
    // 페이지 타이틀 기본값 설정(타이틀 + 앱이름)
    let title = toMeta.title ? " - " + toMeta.title : "";
    // 세션 언어가 영문이면 가능하면 titleEn 사용
    if (sessionStorage.getItem("lang") === "en" && toMeta.titleEn) {
      title = " - " + toMeta.titleEn;
    }
    document.title = appTitle + title;

    /** baseBreadcrumb setting start */
    sidebarStore.setBreadcrumbs([]);
    if (!toMeta.noCrumb) {
      // matched에 있는 라우트 메타들 중 title만 추출
      let matchedTitles = to.matched
        .map((record) => record.meta.title)
        .filter((title): title is string => !!title);

      // 영문 모드이면 titleEn으로 대체 추출
      if (sessionStorage.getItem("lang") === "en" && toMeta.titleEn) {
        matchedTitles = to.matched
          .map((record) => record.meta.titleEn)
          .filter((titleEn): titleEn is string => !!titleEn);
      }

      // 추출한 타이틀들로 breadcrumb 객체 배열 생성(마지막 항목은 disabled)
      const breadcrumbs = matchedTitles.map((title, index) => ({
        title,
        disabled: index === matchedTitles.length - 1,
      })) as [];
      sidebarStore.setBreadcrumbs(breadcrumbs);
    }

    // DOM 업데이트 / 상태 반영 대기
    await nextTick();
  });
};
